import * as IF from "infrontjs";
import {
    engineInit, vec2, Color, clamp,
    drawRect, drawCircle, drawTile, time,
    TextureInfo, TileInfo,
    mousePos, mousePosScreen, mouseIsDown, mouseWasPressed, mouseWasReleased, mouseWheel,
    setCameraPos, setCameraScale,
    setShowSplashScreen, setCanvasMaxSize, setCanvasClearColor,
    setTouchGamepadEnable, setSoundEnable,
    setDebugWatermark,
    mainCanvas
} from 'littlejsengine';
import { MinesweeperGame, CELL_HIDDEN, CELL_REVEALED, CELL_FLAGGED, GAME_CLEAR_ALL, GAME_PATH, GAME_FIND } from '../game/MinesweeperGame.js';
import { getLevelByNumber, getHintForLevel } from '../data/levels.js';
import { getLivesState, loseHeart, gainHeart, getMaxHearts } from '../data/lives.js';
import { RANKS, getRankForLevel } from '../data/ranks.js';
import { addCoins, getCoins, spendCoins } from '../data/coins.js';
import { recordLevelStarted, recordLevelWon, recordLevelLost, markCurrentLevelNotFirstTry } from '../data/stats.js';
import { trackPageView, trackEvent } from '../data/analytics.js';
import { getItem, setItem } from '../data/storage.js';

const LONG_PRESS_MS = 350;
const DRAG_THRESHOLD = 12;
const ZOOM_MIN = 0.75;
const ZOOM_MAX = 3;

// Continue (revive) economy
const CONTINUE_COIN_COST = 200;
const INVITE_REVIVE_LEVEL_RANGE = 100;   // one invite-revive per 100 levels
const INVITE_LAST_USED_KEY = 'minesweeper_last_invite_level';

// Module-level state
let gameStartTime = 0;
let hudElement = null;
let game = null;
let gameConfig = null;
let currentLevel = null;
let engineInitialized = false;
let pressStartTime = null;
let pressStartPos = null;
let pressStartScreen = null;
let longPressTriggered = false;
let isDragging = false;
let lastDragScreen = null;
let gameOverShown = false;
let loseAnimPending = false;
let currentState = null;

// Continue dialog state
let continueUsedForCurrentLevel = false;
let mineHitCell = null;
let preMineHitCellState = null;
let mineHitElapsed = 0;

// Camera zoom / pan
let zoomLevel = 1;
let camOffsetX = 0;
let camOffsetY = 0;
let baseScale = 32;

// Pinch-zoom
let pinchActive = false;
let pinchStartDist = 0;
let pinchStartZoom = 1;
let pinchCooldown = 0;

// Zoom-out animation on game over
let zoomOutActive = false;
let zoomOutStartTime = 0;
let zoomOutStartZoom = 1;
let zoomOutStartOffX = 0;
let zoomOutStartOffY = 0;
const ZOOM_OUT_DURATION = 1;

// Zoom-to-diamond animation (FIND mode win)
let zoomToCellActive = false;
let zoomToCellStartTime = 0;
let zoomToCellStartZoom = 1;
let zoomToCellStartOffX = 0;
let zoomToCellStartOffY = 0;
let zoomToCellTargetZoom = 1;
let zoomToCellTargetOffX = 0;
let zoomToCellTargetOffY = 0;
let zoomToCellCallback = null;
const ZOOM_TO_CELL_DURATION = 1;
const ZOOM_TO_CELL_TARGET = 2.2;

// Shake
let shakeActive = false;
let shakeStartTime = 0;
const SHAKE_DURATION = 0.75;
const SHAKE_INTENSITY = 0.3;

// ============================================================
// Canvas palette — rich Pocket Board tokens for beveled cells
// ============================================================
const COLOR_PLAYFIELD       = new Color( 0.435, 0.545, 0.478 );  // --bg-sage

// Board frame (rounded dark rim around the grid)
const COLOR_FRAME_OUTER     = new Color( 0.176, 0.145, 0.125 );  // --fg-ink
const COLOR_FRAME_INNER     = new Color( 0.272, 0.220, 0.188 );  // warmer inner shadow

// Hidden cell — beveled sage tile
const COLOR_HIDDEN_TOP      = new Color( 0.624, 0.718, 0.650 );  // #9FB7A6 — lit top
const COLOR_HIDDEN_BOT      = new Color( 0.557, 0.662, 0.608 );  // #8EA99B — shaded bottom
const COLOR_HIDDEN_RIM_HI   = new Color( 1.000, 1.000, 1.000, 0.30 );
const COLOR_HIDDEN_RIM_LO   = new Color( 0.156, 0.078, 0.070, 0.32 );

// Flagged cell — lighter cream-sage to stand out
const COLOR_FLAG_TOP        = new Color( 0.788, 0.863, 0.816 );  // #C9DCD0
const COLOR_FLAG_BOT        = new Color( 0.714, 0.800, 0.749 );  // #B6CCBF

// Revealed cell — cream checker
const COLOR_REVEALED_A      = new Color( 0.941, 0.894, 0.784 );  // #F0E4C8 ivory
const COLOR_REVEALED_B      = new Color( 0.910, 0.863, 0.745 );  // #E8DCBE oat
const COLOR_REVEALED_INSET  = new Color( 0.176, 0.145, 0.125, 0.18 );

// Mine (loss) + flag glyph
const COLOR_MINE_BG         = new Color( 0.891, 0.310, 0.227 );
const COLOR_MINE_TEXT       = new Color( 0.176, 0.145, 0.125 );
const COLOR_FLAG_TEXT       = new Color( 0.891, 0.310, 0.227 );

// Path / find accents
const COLOR_START_ZONE      = new Color( 0.435, 0.666, 0.556 );
const COLOR_FINISH_ZONE     = new Color( 0.933, 0.745, 0.353 );
const COLOR_DIAMOND         = new Color( 0.710, 0.596, 0.839 );
const COLOR_ARROW           = new Color( 0.478, 0.373, 0.627 );

// Emboss-text highlight (white below-shadow, mimics CSS `text-shadow: 0 1px 0 rgba(255,255,255,0.35)`)
const COLOR_EMBOSS_HIGHLIGHT = new Color( 1, 1, 1, 0.35 );

// Custom font for canvas text (Nunito loaded via @import in main.css)
const CANVAS_FONT       = '"Nunito", "SF Pro Rounded", sans-serif';
const CANVAS_FONT_STYLE = '900';

// Timer danger threshold — pulse red once time exceeds this many seconds
const TIMER_DANGER_THRESHOLD = 10 * 60;  // 10 minutes

// Board frame / cell geometry (world units)
const FRAME_PAD   = 0.32;   // how far the dark frame extends beyond the cells
const FRAME_RAD   = 0.30;   // frame corner radius
const INNER_PAD   = 0.10;   // inner recess between frame and cell area
const CELL_SIZE   = 0.92;

// Numbers on cream cells (Pocket Board palette)
const NUMBER_COLORS = [
    null,
    new Color( 0.369, 0.612, 0.710 ),  // 1 · sky
    new Color( 0.361, 0.549, 0.431 ),  // 2 · leaf
    new Color( 0.891, 0.310, 0.227 ),  // 3 · tomato
    new Color( 0.478, 0.373, 0.627 ),  // 4 · lilac
    new Color( 0.549, 0.282, 0.275 ),  // 5 · mauve
    new Color( 0.165, 0.541, 0.541 ),  // 6 · teal
    new Color( 0.176, 0.145, 0.125 ),  // 7 · ink
    new Color( 0.353, 0.290, 0.251 ),  // 8 · ink-soft
];

// ============================================================
// Inline SVG snippets (Pocket Board DS)
// ============================================================

const SVG_HEART_FULL = '<svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="_goHF" cx="0.35" cy="0.3" r="0.8"><stop offset="0" stop-color="#F17A67"/><stop offset="0.6" stop-color="#E34F3A"/><stop offset="1" stop-color="#B03324"/></radialGradient></defs><path d="M12 21.2 C8.2 18.4 3 14.8 3 9.8 C3 7.15 5.15 5 7.8 5 C9.6 5 11.05 6 12 7.6 C12.95 6 14.4 5 16.2 5 C18.85 5 21 7.15 21 9.8 C21 14.8 15.8 18.4 12 21.2 Z" fill="url(#_goHF)"/></svg>';
const SVG_HEART_EMPTY = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(45,37,32,0.35)" stroke-width="2" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 21.2 C8.2 18.4 3 14.8 3 9.8 C3 7.15 5.15 5 7.8 5 C9.6 5 11.05 6 12 7.6 C12.95 6 14.4 5 16.2 5 C18.85 5 21 7.15 21 9.8 C21 14.8 15.8 18.4 12 21.2 Z"/></svg>';

const SVG_ARROW_RIGHT = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6 L15 12 L9 18"/></svg>';

const SVG_MINE_SPLAT = '<svg width="84" height="84" viewBox="0 0 84 84"><defs><radialGradient id="_goSplat" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#E34F3A" stop-opacity="0.35"/><stop offset="0.6" stop-color="#E34F3A" stop-opacity="0.12"/><stop offset="1" stop-color="#E34F3A" stop-opacity="0"/></radialGradient></defs><circle cx="42" cy="42" r="40" fill="url(#_goSplat)"/><circle cx="42" cy="42" r="18" fill="#2D2520" stroke="#8C4846" stroke-width="1.5"/><circle cx="36" cy="36" r="4" fill="#5A4A42"/><g stroke="#2D2520" stroke-width="4" stroke-linecap="round"><line x1="42" y1="14" x2="42" y2="22"/><line x1="42" y1="62" x2="42" y2="70"/><line x1="14" y1="42" x2="22" y2="42"/><line x1="62" y1="42" x2="70" y2="42"/><line x1="22" y1="22" x2="28" y2="28"/><line x1="56" y1="56" x2="62" y2="62"/><line x1="22" y1="62" x2="28" y2="56"/><line x1="56" y1="28" x2="62" y2="22"/></g><g stroke="#E34F3A" stroke-width="2" stroke-linecap="round" opacity="0.85"><path d="M8 8 L20 20"/><path d="M76 10 L64 22"/><path d="M10 74 L22 62"/><path d="M74 76 L62 64"/></g></svg>';

const SVG_STAR_BADGE = '<svg width="72" height="72" viewBox="0 0 72 72"><defs><radialGradient id="_lsStarG" cx="0.35" cy="0.3" r="0.8"><stop offset="0" stop-color="#FFE3A8"/><stop offset="0.55" stop-color="#F2B544"/><stop offset="1" stop-color="#B27A1E"/></radialGradient></defs><circle cx="36" cy="36" r="32" fill="rgba(45,37,32,0.15)"/><path d="M36 8 L44 28 L66 30 L49 44 L55 66 L36 54 L17 66 L23 44 L6 30 L28 28 Z" fill="url(#_lsStarG)" stroke="#8C4846" stroke-width="1.8" stroke-linejoin="round"/><path d="M36 14 L41 27 L36 30 L31 27 Z" fill="rgba(255,255,255,0.55)"/></svg>';

const SVG_RANK_STAR = color => `<svg width="16" height="16" viewBox="0 0 24 24"><path d="M12 2 L15 9 L22 10 L17 15 L18 22 L12 18 L6 22 L7 15 L2 10 L9 9 Z" fill="${ color }" stroke="#8C4846" stroke-width="1.2" stroke-linejoin="round"/></svg>`;

const SVG_DOT_TOMATO = '<svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="#E34F3A"/></svg>';

const SVG_COIN_PM = '<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" width="22" height="22"><defs><radialGradient id="_lsCoinFace" cx="0.34" cy="0.28" r="0.95"><stop offset="0" stop-color="#FFE3A8"/><stop offset="0.45" stop-color="#F2B544"/><stop offset="1" stop-color="#A87018"/></radialGradient><linearGradient id="_lsCoinRim" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#D89A2C"/><stop offset="1" stop-color="#7A5212"/></linearGradient></defs><ellipse cx="16" cy="17.5" rx="14" ry="13.5" fill="rgba(40,20,18,0.25)"/><circle cx="16" cy="16" r="14" fill="url(#_lsCoinRim)"/><circle cx="16" cy="16" r="14" fill="none" stroke="#5A3A0E" stroke-width="0.8" opacity="0.6"/><circle cx="16" cy="15.4" r="11.6" fill="url(#_lsCoinFace)"/><circle cx="16" cy="15.4" r="11.6" fill="none" stroke="#8B5B10" stroke-width="0.8" opacity="0.4"/><g transform="translate(16 16.2)"><circle r="5" fill="#3A2A20"/><circle cx="-1.6" cy="-1.6" r="1.4" fill="#7A5C44" opacity="0.85"/><g stroke="#3A2A20" stroke-width="1.5" stroke-linecap="round"><line x1="0" y1="-6.4" x2="0" y2="-7.8"/><line x1="0" y1="6.4" x2="0" y2="7.8"/><line x1="-6.4" y1="0" x2="-7.8" y2="0"/><line x1="6.4" y1="0" x2="7.8" y2="0"/><line x1="-4.6" y1="-4.6" x2="-5.6" y2="-5.6"/><line x1="4.6" y1="4.6" x2="5.6" y2="5.6"/><line x1="-4.6" y1="4.6" x2="-5.6" y2="5.6"/><line x1="4.6" y1="-4.6" x2="5.6" y2="-5.6"/></g><circle cx="3.2" cy="-3.2" r="0.8" fill="#FFE3A8"/></g><ellipse cx="11.5" cy="9.5" rx="4.2" ry="2" fill="#FFF1BE" opacity="0.55"/></svg>';

const SVG_INFO = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7 V13"/><circle cx="12" cy="16.5" r="0.4" fill="currentColor"/></svg>';

const SVG_CLOSE = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6 L18 18"/><path d="M18 6 L6 18"/></svg>';

const SVG_BULB = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="margin-right:6px;vertical-align:-2px"><path d="M12 2 C8 2 5 5 5 9 C5 11.5 6 13 7.5 14.5 L8 18 H16 L16.5 14.5 C18 13 19 11.5 19 9 C19 5 16 2 12 2 Z M9 20 H15 V22 H9 Z"/></svg>';

const SVG_BOMB_MINI = '<svg width="16" height="16" viewBox="0 0 24 24"><circle cx="12" cy="13" r="6.5" fill="#2D2520"/><path d="M14 6 Q17 4 18.5 5" stroke="#F2E9D8" stroke-width="1.6" fill="none" stroke-linecap="round"/><circle cx="19" cy="4.5" r="1.3" fill="#F2B544"/></svg>';

const SVG_FLAG = '<svg width="14" height="14" viewBox="0 0 24 24"><path d="M6 3 L6 21" stroke="#2D2520" stroke-width="2.2" stroke-linecap="round"/><path d="M6 4 L18 7 L6 12 Z" fill="#E34F3A" stroke="#8C4846" stroke-width="1.2" stroke-linejoin="round"/></svg>';

const SVG_CLOCK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"/><path d="M12 8 L12 13 L15 15"/><path d="M9 3 L15 3"/></svg>';

const SVG_HEART_HUD_FULL = '<svg class="pm-hud__heart" width="16" height="16" viewBox="0 0 24 24"><path d="M12 20.5 C6 16.5 2.5 13 2.5 8.5 C2.5 5.8 4.7 3.5 7.5 3.5 C9.5 3.5 11 4.6 12 6.2 C13 4.6 14.5 3.5 16.5 3.5 C19.3 3.5 21.5 5.8 21.5 8.5 C21.5 13 18 16.5 12 20.5 Z" fill="#E34F3A" stroke="#8C4846" stroke-width="1.6" stroke-linejoin="round"/></svg>';

const SVG_HEART_HUD_EMPTY = '<svg class="pm-hud__heart pm-hud__heart--empty" width="16" height="16" viewBox="0 0 24 24"><path d="M12 20.5 C6 16.5 2.5 13 2.5 8.5 C2.5 5.8 4.7 3.5 7.5 3.5 C9.5 3.5 11 4.6 12 6.2 C13 4.6 14.5 3.5 16.5 3.5 C19.3 3.5 21.5 5.8 21.5 8.5 C21.5 13 18 16.5 12 20.5 Z" fill="none" stroke="rgba(45,37,32,0.35)" stroke-width="1.6" stroke-linejoin="round"/></svg>';

const SVG_HEART_FULL_SMALL = '<svg width="14" height="14" viewBox="0 0 24 24"><path d="M12 21.2 C8.2 18.4 3 14.8 3 9.8 C3 7.15 5.15 5 7.8 5 C9.6 5 11.05 6 12 7.6 C12.95 6 14.4 5 16.2 5 C18.85 5 21 7.15 21 9.8 C21 14.8 15.8 18.4 12 21.2 Z" fill="#E34F3A"/></svg>';

const SVG_TROPHY = '<svg width="84" height="84" viewBox="0 0 96 96"><defs><linearGradient id="_trG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FFE3A8"/><stop offset="0.55" stop-color="#F2B544"/><stop offset="1" stop-color="#B27A1E"/></linearGradient></defs><path d="M30 14 H66 V30 C66 46 57 56 48 56 C39 56 30 46 30 30 Z" fill="url(#_trG)" stroke="#8C4846" stroke-width="2"/><path d="M16 22 C16 34 22 36 28 36" fill="none" stroke="#B27A1E" stroke-width="4" stroke-linecap="round"/><path d="M80 22 C80 34 74 36 68 36" fill="none" stroke="#B27A1E" stroke-width="4" stroke-linecap="round"/><rect x="42" y="56" width="12" height="12" fill="#B27A1E"/><rect x="32" y="68" width="32" height="8" rx="2" fill="#8C4846"/><path d="M48 20 L51 27 L58 28 L53 33 L54 40 L48 37 L42 40 L43 33 L38 28 L45 27 Z" fill="rgba(255,255,255,0.45)"/></svg>';

const MODE_GLYPH_SVG = {};
MODE_GLYPH_SVG[ GAME_CLEAR_ALL ] = '<svg width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="13" r="7" fill="#2D2520"/><path d="M14 6 Q17 4 19 5" stroke="#F2E9D8" stroke-width="1.6" fill="none" stroke-linecap="round"/><circle cx="19.5" cy="4.5" r="1.4" fill="#F2B544"/></svg>';
MODE_GLYPH_SVG[ GAME_FIND ] = '<svg width="14" height="14" viewBox="0 0 24 24"><path d="M12 3 L20 12 L12 21 L4 12 Z" fill="#B598D6" stroke="#3E2F5A" stroke-width="1.2"/><path d="M12 3 L15 12 L12 21" stroke="rgba(255,255,255,0.5)" stroke-width="1" fill="none"/></svg>';
MODE_GLYPH_SVG[ GAME_PATH ] = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5C8C6E" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 20 L5 13 L12 13 L12 5"/><circle cx="12" cy="5" r="2" fill="#F2B544" stroke="none"/></svg>';

const MODE_TYPE_GLYPH_LG = {};
MODE_TYPE_GLYPH_LG[ GAME_CLEAR_ALL ] = '<svg width="18" height="18" viewBox="0 0 24 24"><circle cx="12" cy="13" r="7" fill="#2D2520"/><path d="M14 6 Q17 4 19 5" stroke="#F2E9D8" stroke-width="1.8" fill="none" stroke-linecap="round"/><circle cx="19.5" cy="4.5" r="1.5" fill="#F2B544"/></svg>';
MODE_TYPE_GLYPH_LG[ GAME_FIND ] = '<svg width="18" height="18" viewBox="0 0 24 24"><path d="M12 3 L20 12 L12 21 L4 12 Z" fill="#B598D6" stroke="#8C4846" stroke-width="1"/><path d="M12 3 L15 12 L12 21" stroke="rgba(255,255,255,0.45)" stroke-width="1" fill="none"/></svg>';
MODE_TYPE_GLYPH_LG[ GAME_PATH ] = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5C8C6E" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 20 L5 15 L10 15 L10 9 L15 9 L15 4"/><circle cx="15" cy="4" r="2" fill="#F2B544" stroke="none"/><circle cx="5" cy="20" r="2" fill="#5C8C6E" stroke="none"/></svg>';

const DIFFICULTY_COLOR = {
    Beginner:     '#5C8C6E',
    Intermediate: '#5E9CB5',
    Advanced:     '#F2B544',
    Expert:       '#E34F3A',
    Master:       '#B598D6',
};

const GAME_TYPE_LABELS = {
    [ GAME_CLEAR_ALL ]: 'CLEAR ALL',
    [ GAME_PATH ]:      'PATH',
    [ GAME_FIND ]:      'FIND',
};

// ============================================================
// Mode tutorials (milestone levels)
// ============================================================

const TUT_ART_CLEAR_ALL = '<svg viewBox="0 0 120 120" width="84" height="84"><defs><radialGradient id="_tb" cx="0.38" cy="0.35" r="0.7"><stop offset="0" stop-color="#6A5A4E"/><stop offset="0.6" stop-color="#2D2520"/><stop offset="1" stop-color="#14100C"/></radialGradient></defs><circle cx="60" cy="70" r="34" fill="url(#_tb)"/><ellipse cx="48" cy="58" rx="10" ry="6" fill="#FAF4E6" opacity="0.85"/><path d="M70 40 Q82 24 94 28" stroke="#F2E9D8" stroke-width="4" fill="none" stroke-linecap="round"/><circle cx="96" cy="26" r="7" fill="#F2B544"/><circle cx="96" cy="26" r="3" fill="#FAF4E6"/></svg>';

const TUT_ART_FIND = '<svg viewBox="0 0 120 120" width="84" height="84"><defs><linearGradient id="_tdia" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#B598D6"/><stop offset="1" stop-color="#7A5FA0"/></linearGradient></defs><path d="M60 14 L100 60 L60 106 L20 60 Z" fill="url(#_tdia)" stroke="#3E2F5A" stroke-width="2"/><path d="M60 14 L75 60 L60 106" stroke="rgba(255,255,255,0.45)" stroke-width="2" fill="none"/><path d="M20 60 L100 60" stroke="rgba(255,255,255,0.22)" stroke-width="1.5"/><path d="M40 38 Q48 32 56 36" stroke="rgba(255,255,255,0.6)" stroke-width="2" fill="none" stroke-linecap="round"/></svg>';

const TUT_ART_PATH = '<svg viewBox="0 0 120 120" width="84" height="84" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="18" y="90" width="84" height="14" rx="4" fill="#5C8C6E" opacity="0.28"/><rect x="18" y="16" width="84" height="14" rx="4" fill="#F2B544" opacity="0.32"/><path d="M30 96 L30 70 L54 70 L54 50 L86 50 L86 24" stroke="#FAF4E6" stroke-width="4"/><circle cx="30" cy="96" r="7" fill="#5C8C6E"/><path d="M86 14 L86 26 M80 20 L86 14 L92 20" stroke="#F2B544" stroke-width="4"/></svg>';

const TUT_ART_HEART = '<svg viewBox="0 0 120 120" width="84" height="84"><defs><radialGradient id="_thg" cx="0.35" cy="0.3" r="0.75"><stop offset="0" stop-color="#F17A67"/><stop offset="0.6" stop-color="#E34F3A"/><stop offset="1" stop-color="#B03324"/></radialGradient></defs><path d="M60 104 C30 84 8 64 8 40 C8 26 20 14 34 14 C44 14 54 20 60 30 C66 20 76 14 86 14 C100 14 112 26 112 40 C112 64 90 84 60 104 Z" fill="url(#_thg)"/><path d="M28 34 C32 28 40 24 46 26" stroke="#FAF4E6" stroke-width="3.5" fill="none" stroke-linecap="round" opacity="0.85"/><g fill="#F2B544"><path d="M90 48 l3 6 6 1 -4.5 4 1 6 -5.5 -3 -5.5 3 1 -6 -4.5 -4 6 -1 z"/></g></svg>';

const TUT_ART_ZOOM = '<svg viewBox="0 0 120 120" width="84" height="84" fill="none" stroke-linecap="round" stroke-linejoin="round"><circle cx="50" cy="50" r="28" stroke="#FAF4E6" stroke-width="6"/><path d="M71 71 L96 96" stroke="#FAF4E6" stroke-width="8"/><path d="M40 50 L60 50 M50 40 L50 60" stroke="#F2B544" stroke-width="4"/></svg>';

function buildEdgeArt()
{
    let rects = '';
    for ( let x = 0; x < 6; x++ )
    {
        for ( let y = 0; y < 6; y++ )
        {
            const isEdge = ( x === 0 || y === 0 || x === 5 || y === 5 );
            const isCorner = ( ( x === 0 || x === 5 ) && ( y === 0 || y === 5 ) );
            const fill = isCorner ? '#2D2520' : isEdge ? 'rgba(45,37,32,0.55)' : '#6F8B7A';
            const stroke = isCorner ? 'none' : 'rgba(250,244,230,0.2)';
            rects += `<rect x="${ 6 + x * 18 }" y="${ 6 + y * 18 }" width="16" height="16" rx="3" fill="${ fill }" stroke="${ stroke }" stroke-width="1"/>`;
        }
    }
    return `<svg viewBox="0 0 120 120" width="84" height="84">${ rects }</svg>`;
}

const MODE_TUTORIALS = {
    1: {
        art: TUT_ART_CLEAR_ALL, title: 'CLEAR ALL Mode', subtitle: 'How it works',
        accent: '#F2B544',
        lines: [
            'Tap a cell to reveal it. Numbers show how many mines touch that cell.',
            'Use the numbers to figure out which cells are safe.',
            'Long-press or right-click to flag a cell you think hides a mine.',
            'Reveal every safe cell without hitting a mine to win!',
        ],
    },
    16: {
        art: TUT_ART_FIND, title: 'FIND Mode', subtitle: 'New game mode unlocked!',
        accent: '#B598D6',
        lines: [
            'A hidden diamond is buried somewhere on the board.',
            'Reveal cells to search for it — empty cells show an arrow pointing toward the diamond.',
            'Use the arrows to narrow down its location.',
            'The game is won the moment you reveal the diamond!',
        ],
    },
    20: {
        art: TUT_ART_HEART, title: 'Heart Refill', subtitle: 'New reward unlocked!',
        accent: '#E34F3A',
        lines: [
            'Every 10 levels you complete, you get one life back!',
            'Milestones: level 20, 30, 40, 50 and so on.',
            'Keep pushing forward to stay stocked on lives.',
        ],
    },
    31: {
        art: TUT_ART_PATH, title: 'PATH Mode', subtitle: 'New game mode unlocked!',
        accent: '#5C8C6E',
        lines: [
            'Find a safe path from the bottom row to the top row.',
            'The bottom row is the start zone (green) and the top row is the finish zone (gold).',
            'Reveal cells one step at a time to carve your route upward.',
            'Reach any cell in the top row without hitting a mine to win!',
        ],
    },
    89: {
        art: TUT_ART_ZOOM, title: 'Zoom & Pan', subtitle: 'New control unlocked!',
        accent: '#5E9CB5',
        lines: [
            'The board is getting bigger! You can now zoom in and out.',
            'On mobile, use a pinch gesture to zoom.',
            'On desktop, use the mouse wheel to zoom.',
            'While zoomed in, click and drag to pan around the board.',
        ],
    },
    100: {
        art: buildEdgeArt(), title: 'Edge Blocks', subtitle: 'New challenge unlocked!',
        accent: '#8C4846',
        lines: [
            'Some cells along the edges are now blocked and cannot be revealed.',
            'This narrows the playing field and limits your options.',
            'Blocked cells increase in later levels — adapt your strategy!',
        ],
    },
};

// ============================================================
// Helpers
// ============================================================

function renderHeartsRowHtml( hearts, max, filledSvg, emptySvg )
{
    let html = '';
    for ( let i = 0; i < max; i++ )
    {
        html += ( i < hearts ? filledSvg : emptySvg );
    }
    return html;
}

function renderConfettiHtml()
{
    const colors = ['#F2B544','#E34F3A','#5C8C6E','#B598D6','#5E9CB5'];
    let html = '';
    for ( let i = 0; i < 18; i++ )
    {
        const c = colors[ i % colors.length ];
        const left = ( i * 6.2 ) % 100;
        const delay = ( i % 7 ) * 0.12;
        const dur = 2 + ( i % 4 ) * 0.4;
        const shape = i % 3;
        const bg = shape === 2 ? 'transparent' : c;
        html += `<span class="pm-ls__confetti-piece pm-ls__confetti-piece--${ shape }" style="left:${ left }%;background:${ bg };border-color:${ c };animation-delay:${ delay }s;animation-duration:${ dur }s;"></span>`;
    }
    return html;
}

function resetGame()
{
    game = new MinesweeperGame( gameConfig.w, gameConfig.h, gameConfig.mines, gameConfig.type, gameConfig.seed, gameConfig.edgeDecorationLevel );
    gameOverShown = false;
    pressStartTime = null;
    longPressTriggered = false;
    isDragging = false;
    zoomLevel = 1;
    camOffsetX = 0;
    camOffsetY = 0;
    zoomOutActive = false;
    zoomToCellActive = false;
    zoomToCellCallback = null;
    loseAnimPending = false;
    continueUsedForCurrentLevel = false;
    mineHitCell = null;
    preMineHitCellState = null;
    mineHitElapsed = 0;
    gameStartTime = time;
    if ( currentLevel ) recordLevelStarted( currentLevel.levelNumber );
    if ( currentLevel ) trackEvent( 'level_started', { level: currentLevel.levelNumber } );
    showGameHud();
}

// Count revealed (non-disabled) cells in a cellState snapshot. Used at level
// end to attribute "cleared fields" to the player. On loss we pass the
// pre-mine-hit snapshot so the auto reveal-all isn't counted.
function countRevealedCells( cellState )
{
    if ( !cellState || !game ) return 0;
    let n = 0;
    for ( let x = 0; x < game.width; x++ )
    {
        for ( let y = 0; y < game.height; y++ )
        {
            if ( game.disabled[ x ][ y ] ) continue;
            if ( cellState[ x ][ y ] === CELL_REVEALED ) n++;
        }
    }
    return n;
}

function loadNextLevel()
{
    const cleared = parseInt( getItem( 'minesweeper_cleared' ) || '0', 10 );
    const levelNum = Math.min( cleared + 1, 999 );
    currentLevel = getLevelByNumber( levelNum ) || getLevelByNumber( 1 );
    gameConfig = {
        w: currentLevel.width,
        h: currentLevel.height,
        mines: currentLevel.numMines,
        type: currentLevel.type,
        seed: currentLevel.seed,
        edgeDecorationLevel: currentLevel.edgeDecorationLevel,
    };
    game = null;
    removeGameHud();

    if ( currentLevel && MODE_TUTORIALS[ currentLevel.levelNumber ] )
    {
        showModeTutorialOverlay( currentLevel.levelNumber, () => showLevelIntroOverlay() );
    }
    else
    {
        showLevelIntroOverlay();
    }
}

function gridFromPos( pos )
{
    if ( !gameConfig ) return null;
    const gx = Math.floor( pos.x );
    const gy = Math.floor( pos.y );
    if ( gx >= 0 && gx < gameConfig.w && gy >= 0 && gy < gameConfig.h )
    {
        if ( game && game.disabled[ gx ][ gy ] ) return null;
        if ( game && game.frozen[ gx ][ gy ] ) return null;
        return { x: gx, y: gy };
    }
    return null;
}

// ============================================================
// Game-over routing
// ============================================================

function showGameOverOverlay( won, livesAfterLoss )
{
    const lvl = currentLevel ? currentLevel.levelNumber : 1;

    if ( won )
    {
        const elapsed = Math.max( 0, Math.floor( time - gameStartTime ) );
        recordLevelWon( {
            mines: gameConfig ? gameConfig.mines : 0,
            clearedCells: game ? countRevealedCells( game.cellState ) : 0,
            elapsedSeconds: elapsed,
        });
    }
    else
    {
        const elapsed = Math.max( 0, Math.floor( mineHitElapsed > 0 ? mineHitElapsed : ( time - gameStartTime ) ) );
        const snapshot = preMineHitCellState || ( game ? game.cellState : null );
        recordLevelLost( {
            clearedCells: countRevealedCells( snapshot ),
            elapsedSeconds: elapsed,
        });
    }

    if ( won && currentLevel )
    {
        const stored = parseInt( getItem( 'minesweeper_cleared' ) || '0', 10 );
        if ( lvl > stored )
        {
            setItem( 'minesweeper_cleared', String( lvl ) );
        }
    }

    const isLastLevel = lvl >= 999;
    if ( won && isLastLevel )
    {
        showMinesMasterOverlay();
        return;
    }

    // FIND / PATH mode win: zoom into the winning cell for 1 second, then show the overlay.
    if ( won && game && gameConfig )
    {
        let focusX = -1, focusY = -1;
        if ( gameConfig.type === GAME_FIND && game.diamondX >= 0 && game.diamondY >= 0 )
        {
            focusX = game.diamondX;
            focusY = game.diamondY;
        }
        else if ( gameConfig.type === GAME_PATH )
        {
            const topY = gameConfig.h - 1;
            for ( let x = 0; x < gameConfig.w; x++ )
            {
                if ( !game.disabled[ x ][ topY ] && game.cellState[ x ][ topY ] === CELL_REVEALED )
                {
                    focusX = x;
                    focusY = topY;
                    break;
                }
            }
        }

        if ( focusX >= 0 )
        {
            zoomOutActive = false;
            zoomToCellActive = true;
            zoomToCellStartTime = time;
            zoomToCellStartZoom = zoomLevel;
            zoomToCellStartOffX = camOffsetX;
            zoomToCellStartOffY = camOffsetY;
            zoomToCellTargetZoom = Math.min( ZOOM_MAX, ZOOM_TO_CELL_TARGET );
            zoomToCellTargetOffX = ( focusX + 0.5 ) - gameConfig.w / 2;
            zoomToCellTargetOffY = ( focusY + 0.5 ) - gameConfig.h / 2;
            zoomToCellCallback = () => showWinOverlay( lvl );
            return;
        }
    }

    // Start zoom-out animation if zoomed in
    if ( zoomLevel > 1.01 )
    {
        zoomOutActive = true;
        zoomOutStartTime = time;
        zoomOutStartZoom = zoomLevel;
        zoomOutStartOffX = camOffsetX;
        zoomOutStartOffY = camOffsetY;
    }

    if ( won )
    {
        showWinOverlay( lvl );
    }
    else
    {
        showLoseOverlay( lvl, livesAfterLoss );
    }
}

function showWinOverlay( lvl )
{
    const num = String( lvl ).padStart( 3, '0' );
    const mode = gameConfig ? gameConfig.type : GAME_CLEAR_ALL;
    const subtitle =
        mode === GAME_PATH ? 'Path complete!' :
        mode === GAME_FIND ? 'Diamond found!' :
                             'All mines avoided!';

    const elapsed = Math.min( 999, Math.max( 0, Math.floor( time - gameStartTime ) ) );
    const timeText = Math.floor( elapsed / 60 ) + ':' + String( elapsed % 60 ).padStart( 2, '0' );

    let flagsPlaced = 0;
    if ( game )
    {
        for ( let x = 0; x < game.width; x++ )
            for ( let y = 0; y < game.height; y++ )
                if ( game.cellState[ x ][ y ] === CELL_FLAGGED ) flagsPlaced++;
    }

    // Time bonus: level number (in seconds) − elapsed seconds, awarded as coins.
    const coinBonus = Math.max( 0, lvl - elapsed );
    const hasBonus = coinBonus > 0;
    if ( hasBonus ) addCoins( coinBonus );

    const fmtBonusSec = n => String( Math.max( 0, n | 0 ) ).padStart( 3, '0' ) + 's';
    const bonusHtml = hasBonus
        ? `<div class="pm-ls__bonus is-on" aria-label="${ coinBonus } coin time bonus">
                <div class="pm-ls__bonusRow">
                    <span class="pm-ls__bonusTerm">${ fmtBonusSec( lvl ) }</span>
                    <span class="pm-ls__bonusOp" aria-hidden="true">&minus;</span>
                    <span class="pm-ls__bonusTerm">${ fmtBonusSec( elapsed ) }</span>
                    <span class="pm-ls__bonusOp" aria-hidden="true">=</span>
                    <span class="pm-ls__bonusResult">
                        <span class="pm-ls__bonusCoin">${ SVG_COIN_PM }</span>
                        <span class="pm-ls__bonusNum" id="pm-ls-bonus-num" data-target="${ coinBonus }">000</span>
                        <span class="pm-ls__bonusUnit">${ coinBonus === 1 ? 'Coin' : 'Coins' }</span>
                    </span>
                </div>
            </div>`
        : `<div class="pm-ls__bonus is-off" aria-label="No time bonus">
                <div class="pm-ls__bonusRow pm-ls__bonusRow--none">
                    ${ SVG_INFO }
                    <span>No time bonus</span>
                </div>
            </div>`;

    let heartRefillHtml = '';
    if ( lvl >= 20 && lvl % 10 === 0 )
    {
        const before = getLivesState().hearts;
        const after = gainHeart();
        if ( after.hearts > before )
        {
            heartRefillHtml = `<div class="pm-ls__heartRefill">${ SVG_HEART_FULL_SMALL } +1 Life restored!</div>`;
        }
    }

    const prevInfo = getRankForLevel( lvl - 1 );
    const newInfo  = getRankForLevel( lvl );
    const rankedUp = prevInfo.rankIndex !== newInfo.rankIndex;
    const displayInfo = rankedUp ? prevInfo : newInfo;
    const displayLevelInRank = rankedUp ? prevInfo.rankSize : newInfo.levelInRank;
    const displayRankSize    = rankedUp ? prevInfo.rankSize : newInfo.rankSize;
    const accent = '#F2B544';

    const overlay = document.createElement( 'div' );
    overlay.className = 'pm-overlay pm-ls';
    overlay.id = 'game-over-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-ls__confetti" aria-hidden="true">${ renderConfettiHtml() }</div>
        <div class="pm-ls__card" style="--accent:${ accent }">
            <div class="pm-ls__eyebrow">${ SVG_DOT_TOMATO } LEVEL #${ num } · SOLVED</div>
            <div class="pm-ls__hero">${ SVG_STAR_BADGE }</div>
            <h2 class="pm-ls__title">You Win!</h2>
            <p class="pm-ls__subtitle">${ subtitle }</p>

            <div class="pm-ls__stats">
                <div class="pm-ls__stat">
                    <span class="pm-ls__statLabel">TIME</span>
                    <span class="pm-ls__statValue">${ timeText }</span>
                </div>
                <div class="pm-ls__stat">
                    <span class="pm-ls__statLabel">MINES</span>
                    <span class="pm-ls__statValue">${ gameConfig.mines }</span>
                </div>
                <div class="pm-ls__stat">
                    <span class="pm-ls__statLabel">FLAGS</span>
                    <span class="pm-ls__statValue">${ flagsPlaced }</span>
                </div>
            </div>

            ${ bonusHtml }

            ${ heartRefillHtml }

            <div class="pm-ls__rank">
                <div class="pm-ls__rankLine">
                    <span class="pm-ls__rankIc">${ SVG_RANK_STAR( accent ) }</span>
                    <span class="pm-ls__rankName" id="pm-ls-rank-name">${ displayInfo.rank.name }</span>
                    <span class="pm-ls__rankCount" id="pm-ls-rank-count">${ displayLevelInRank } / ${ displayRankSize }</span>
                </div>
                <div class="pm-ls__rankBar">
                    <div class="pm-ls__rankFill" id="pm-ls-rank-fill" style="width:0%"></div>
                </div>
            </div>

            <div class="pm-ls__actions">
                <button class="pm-ls__next" id="btn-play-again">
                    <span>Next Level</span>
                    ${ SVG_ARROW_RIGHT }
                </button>
                <button class="pm-ls__menu" id="btn-back-menu">Back to Menu</button>
            </div>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    if ( hasBonus )
    {
        const bonusEl = document.getElementById( 'pm-ls-bonus-num' );
        if ( bonusEl )
        {
            const target = coinBonus;
            const duration = 1000;
            const startAt = performance.now();
            const tick = ( now ) =>
            {
                const t = Math.min( 1, ( now - startAt ) / duration );
                const eased = 1 - Math.pow( 1 - t, 3 );
                const value = Math.round( target * eased );
                bonusEl.textContent = String( value ).padStart( 3, '0' );
                if ( t < 1 ) requestAnimationFrame( tick );
                else bonusEl.textContent = String( target ).padStart( 3, '0' );
            };
            requestAnimationFrame( tick );
        }
    }

    setTimeout( () =>
    {
        const fill = document.getElementById( 'pm-ls-rank-fill' );
        if ( !fill ) return;
        if ( rankedUp )
        {
            fill.style.width = '100%';
            setTimeout( () => showRankUpOverlay( newInfo ), 900 );
        }
        else
        {
            fill.style.width = ( newInfo.progress * 100 ) + '%';
        }
    }, 450 );

    document.getElementById( 'btn-play-again' ).addEventListener( 'click', () =>
    {
        removeGameOverOverlay();
        loadNextLevel();
    });
    document.getElementById( 'btn-back-menu' ).addEventListener( 'click', () =>
    {
        removeGameOverOverlay();
        if ( currentState ) currentState.app.router.redirect( '/' );
    });
}

function showLoseOverlay( lvl, livesAfterLoss )
{
    const num = String( lvl ).padStart( 3, '0' );
    const isFinal = livesAfterLoss !== undefined && livesAfterLoss <= 0;
    const maxLives = getMaxHearts();
    const livesLeft = livesAfterLoss !== undefined ? livesAfterLoss : 0;

    const subtitle = gameConfig && gameConfig.type === GAME_PATH
        ? 'You stepped off the path!'
        : 'You hit a mine!';

    const retryBtn = isFinal
        ? ''
        : `<button class="pm-go__retry" id="btn-play-again">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12 a 9 9 0 1 0 3-6.7"/><path d="M3 3 V 7 H 7"/></svg>
                <span>Try Again</span>
            </button>`;

    const overlay = document.createElement( 'div' );
    overlay.className = `pm-overlay pm-go${ isFinal ? ' pm-go--final' : '' }`;
    overlay.id = 'game-over-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-go__card">
            <div class="pm-go__eyebrow">${ SVG_DOT_TOMATO } LEVEL #${ num } · ${ isFinal ? 'FAILED' : 'MINE HIT' }</div>
            <div class="pm-go__hero">${ SVG_MINE_SPLAT }</div>
            <h2 class="pm-go__title">Game Over</h2>
            <p class="pm-go__subtitle">${ isFinal ? 'No lives remaining.' : subtitle }</p>
            <div class="pm-go__lives">
                ${ renderHeartsRowHtml( livesLeft, maxLives, SVG_HEART_FULL, SVG_HEART_EMPTY ) }
            </div>
            <div class="pm-go__livesLabel">
                ${ isFinal ? 'All lives used' : livesLeft + ' ' + ( livesLeft === 1 ? 'life' : 'lives' ) + ' left' }
            </div>
            <div class="pm-go__actions">
                ${ retryBtn }
                <button class="pm-go__menu" id="btn-back-menu">Back to Menu</button>
            </div>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    const playAgainBtn = document.getElementById( 'btn-play-again' );
    if ( playAgainBtn )
    {
        playAgainBtn.addEventListener( 'click', () =>
        {
            removeGameOverOverlay();
            resetGame();
        });
    }
    document.getElementById( 'btn-back-menu' ).addEventListener( 'click', () =>
    {
        removeGameOverOverlay();
        if ( currentState ) currentState.app.router.redirect( '/' );
    });
}

function showMinesMasterOverlay()
{
    removeGameHud();

    if ( zoomLevel > 1.01 )
    {
        zoomOutActive = true;
        zoomOutStartTime = time;
        zoomOutStartZoom = zoomLevel;
        zoomOutStartOffX = camOffsetX;
        zoomOutStartOffY = camOffsetY;
    }

    const overlay = document.createElement( 'div' );
    overlay.className = 'pm-overlay pm-mm';
    overlay.id = 'mines-master-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-mm__card">
            <div class="pm-mm__icon">${ SVG_TROPHY }</div>
            <h2 class="pm-mm__title">Mines Master</h2>
            <p class="pm-mm__text">You successfully cleared all 999 levels!</p>
            <p class="pm-mm__subtext">You are a true Mines Master.</p>
            <button class="pm-lvl__start" id="btn-master-menu"><span>Back to Menu</span>${ SVG_ARROW_RIGHT }</button>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    document.getElementById( 'btn-master-menu' ).addEventListener( 'click', () =>
    {
        overlay.remove();
        if ( currentState ) currentState.app.router.redirect( '/' );
    });
}

function removeGameOverOverlay()
{
    const el = document.getElementById( 'game-over-overlay' );
    if ( el ) el.remove();
    const rankUp = document.getElementById( 'rank-up-overlay' );
    if ( rankUp ) rankUp.remove();
}

function showRankUpOverlay( newInfo )
{
    const bonusText = newInfo.rank.bonus
        ? `<p class="pm-ru__bonus">${ newInfo.rank.bonus }</p>`
        : '';

    const overlay = document.createElement( 'div' );
    overlay.className = 'pm-overlay pm-ru';
    overlay.id = 'rank-up-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-ru__card">
            <div class="pm-ru__icon">${ SVG_STAR_BADGE }</div>
            <h2 class="pm-ru__title">Rank Up!</h2>
            <p class="pm-ru__name">${ newInfo.rank.name }</p>
            ${ bonusText }
            <button class="pm-ru__btn" id="btn-rank-up-continue"><span>Continue</span>${ SVG_ARROW_RIGHT }</button>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    document.getElementById( 'btn-rank-up-continue' ).addEventListener( 'click', () =>
    {
        overlay.remove();
        const fill = document.getElementById( 'pm-ls-rank-fill' );
        if ( fill ) fill.style.width = ( newInfo.progress * 100 ) + '%';
        const nameEl = document.getElementById( 'pm-ls-rank-name' );
        if ( nameEl ) nameEl.textContent = newInfo.rank.name;
        const countEl = document.getElementById( 'pm-ls-rank-count' );
        if ( countEl ) countEl.textContent = newInfo.levelInRank + ' / ' + newInfo.rankSize;
    });
}

// ============================================================
// Continue (revive) overlay — shown after a mine hit, before
// routing to the Game Over screen. Offers up to three revive
// paths (pay coins, watch ad, invite a friend) plus a graceful
// decline that falls through to the normal Game Over flow.
// ============================================================

const SVG_CN_HEART_PLUS = '<svg width="92" height="92" viewBox="0 0 92 92" aria-hidden="true"><defs><radialGradient id="_cnHaloG" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#F2B544" stop-opacity="0.45"/><stop offset="0.6" stop-color="#F2B544" stop-opacity="0.12"/><stop offset="1" stop-color="#F2B544" stop-opacity="0"/></radialGradient><linearGradient id="_cnHeartG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FF7A6B"/><stop offset="1" stop-color="#C13B2B"/></linearGradient></defs><circle cx="46" cy="46" r="44" fill="url(#_cnHaloG)"/><path d="M46 78 C 22 60, 14 50, 14 36 A 14 14 0 0 1 46 28 A 14 14 0 0 1 78 36 C 78 50, 70 60, 46 78 Z" fill="url(#_cnHeartG)" stroke="#8C4846" stroke-width="2.4" stroke-linejoin="round" style="filter: drop-shadow(0 6px 8px rgba(40,20,18,0.35))"/><path d="M30 30 C 32 24, 40 22, 44 28" stroke="#FFE3A8" stroke-width="3" stroke-linecap="round" fill="none" opacity="0.7"/><g transform="translate(64 24)"><circle r="14" fill="#F2B544" stroke="#8C4846" stroke-width="2"/><path d="M-7 0 H 7 M 0 -7 V 7" stroke="#2D2520" stroke-width="3.4" stroke-linecap="round"/></g></svg>';

const SVG_CN_AD = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 17 V 7 a2 2 0 0 1 2 -2 h 8 l 5 4 v 8 a 2 2 0 0 1 -2 2 H 7 a 2 2 0 0 1 -2 -2 z"/><path d="M10 10 L 14 12 L 10 14 Z" fill="currentColor"/></svg>';

const SVG_CN_SHARE = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="6" cy="12" r="2.5"/><circle cx="17" cy="6" r="2.5"/><circle cx="17" cy="18" r="2.5"/><path d="M8 11 L 15 7"/><path d="M8 13 L 15 17"/></svg>';

function getInviteLastUsedLevel()
{
    const raw = getItem( INVITE_LAST_USED_KEY );
    const n = parseInt( raw, 10 );
    return Number.isFinite( n ) ? n : -INVITE_REVIVE_LEVEL_RANGE;
}

function isInviteReviveAvailable( lvl )
{
    const last = getInviteLastUsedLevel();
    return ( lvl - last ) >= INVITE_REVIVE_LEVEL_RANGE;
}

function showContinueOverlay()
{
    if ( document.getElementById( 'continue-overlay' ) ) return;

    const lvl = currentLevel ? currentLevel.levelNumber : 1;
    const num = String( lvl ).padStart( 3, '0' );
    const wallet = getCoins();
    const canPay = wallet >= CONTINUE_COIN_COST;
    const canInvite = isInviteReviveAvailable( lvl );

    const payBtn = canPay
        ? `<button class="pm-cn__opt pm-cn__opt--coins is-primary" id="btn-cn-pay">
                <span class="pm-cn__optBadge pm-cn__optBadge--coin">${ SVG_COIN_PM }</span>
                <span class="pm-cn__optBody">
                    <span class="pm-cn__optTitle">Pay coins</span>
                    <span class="pm-cn__optSub">Use your wallet</span>
                </span>
                <span class="pm-cn__optMeta">
                    <span class="pm-cn__cost">
                        <span class="pm-cn__costNum">${ CONTINUE_COIN_COST }</span>
                        <span class="pm-cn__costUnit">coins</span>
                    </span>
                </span>
            </button>`
        : '';

    const adBtn = `<button class="pm-cn__opt pm-cn__opt--ad" id="btn-cn-ad" style="display:none">
            <span class="pm-cn__optBadge">${ SVG_CN_AD }</span>
            <span class="pm-cn__optBody">
                <span class="pm-cn__optTitle">Watch a short ad</span>
                <span class="pm-cn__optSub">Earn one revive</span>
            </span>
            <span class="pm-cn__optMeta"><span class="pm-cn__free">FREE</span></span>
        </button>`;

    const shareBtn = canInvite
        ? `<button class="pm-cn__opt pm-cn__opt--share" id="btn-cn-share">
                <span class="pm-cn__optBadge">${ SVG_CN_SHARE }</span>
                <span class="pm-cn__optBody">
                    <span class="pm-cn__optTitle">Invite a friend</span>
                    <span class="pm-cn__optSub">Share Puzzlemines to revive</span>
                </span>
                <span class="pm-cn__optMeta"><span class="pm-cn__free">FREE</span></span>
            </button>`
        : '';

    const overlay = document.createElement( 'div' );
    overlay.className = 'pm-overlay pm-cn';
    overlay.id = 'continue-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-cn__card" role="dialog" aria-modal="true" aria-labelledby="pm-cn-title">
            <div class="pm-cn__top">
                <div class="pm-cn__eyebrow">${ SVG_DOT_TOMATO } LEVEL #${ num } · MINE HIT</div>
            </div>
            <div class="pm-cn__hero">${ SVG_CN_HEART_PLUS }</div>
            <h2 class="pm-cn__title" id="pm-cn-title">Continue?</h2>
            <p class="pm-cn__subtitle">One more chance to clear this level.</p>
            <div class="pm-cn__opts">
                ${ payBtn }
                ${ adBtn }
                ${ shareBtn }
            </div>
            <button class="pm-cn__decline" id="btn-cn-decline">No thanks — End run</button>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    if ( canPay )
    {
        document.getElementById( 'btn-cn-pay' ).addEventListener( 'click', () =>
        {
            if ( !spendCoins( CONTINUE_COIN_COST ) ) return;
            performContinueRevive();
        });
    }
    document.getElementById( 'btn-cn-ad' ).addEventListener( 'click', () =>
    {
        // Reward ad placeholder — no ad network wired up yet.
        performContinueRevive();
    });
    if ( canInvite )
    {
        document.getElementById( 'btn-cn-share' ).addEventListener( 'click', async () =>
        {
            const shareUrl   = 'https://www.puzzlemines.com';
            const shareTitle = 'Puzzlemines';
            const shareText  = 'Play Puzzlemines — a daily minesweeper quest!';

            const finishRevive = () =>
            {
                setItem( INVITE_LAST_USED_KEY, String( lvl ) );
                performContinueRevive();
            };

            const baseShare = { title: shareTitle, text: shareText, url: shareUrl };
            const iconFile  = await getShareIconFile();
            // Only attach the icon if the platform actually accepts files in
            // the share payload — otherwise navigator.share rejects outright.
            const shareData = ( iconFile && navigator.canShare && navigator.canShare( { ...baseShare, files: [ iconFile ] } ) )
                ? { ...baseShare, files: [ iconFile ] }
                : baseShare;

            if ( navigator.share && ( !navigator.canShare || navigator.canShare( shareData ) ) )
            {
                try
                {
                    await navigator.share( shareData );
                    finishRevive();
                }
                catch ( err )
                {
                    // User dismissed the share sheet — leave overlay open so they can pick another option.
                    if ( err && err.name === 'AbortError' ) return;
                    // Any other failure: fall through to clipboard fallback below.
                    copyShareUrlFallback( shareUrl, finishRevive );
                }
                return;
            }

            copyShareUrlFallback( shareUrl, finishRevive );
        });
    }
    document.getElementById( 'btn-cn-decline' ).addEventListener( 'click', () =>
    {
        removeContinueOverlay();
        triggerMineHitFinale();
    });
}

function removeContinueOverlay()
{
    const el = document.getElementById( 'continue-overlay' );
    if ( el ) el.remove();
}

// Lazily fetch the PWA icon, rasterize it to a PNG File, and cache the
// resulting promise so subsequent shares are instant. Passing this File via
// navigator.share's `files` array makes the OS share sheet display the
// icon as the preview image instead of a generic placeholder.
let _shareIconFilePromise = null;
function getShareIconFile()
{
    if ( _shareIconFilePromise ) return _shareIconFilePromise;

    _shareIconFilePromise = ( async () =>
    {
        try
        {
            const res = await fetch( '/assets/icons/puzzlemines-icon.svg' );
            if ( !res.ok ) return null;
            const svgText = await res.text();
            const svgBlob = new Blob( [ svgText ], { type: 'image/svg+xml' } );
            const objectUrl = URL.createObjectURL( svgBlob );

            const img = new Image();
            await new Promise( ( resolve, reject ) =>
            {
                img.onload  = resolve;
                img.onerror = reject;
                img.src     = objectUrl;
            });

            const size = 512;
            const canvas = document.createElement( 'canvas' );
            canvas.width  = size;
            canvas.height = size;
            const ctx = canvas.getContext( '2d' );
            ctx.drawImage( img, 0, 0, size, size );
            URL.revokeObjectURL( objectUrl );

            const pngBlob = await new Promise( resolve => canvas.toBlob( resolve, 'image/png' ) );
            if ( !pngBlob ) return null;

            return new File( [ pngBlob ], 'puzzlemines.png', { type: 'image/png' } );
        }
        catch ( err )
        {
            console.warn( 'share-icon rasterization failed', err );
            // Reset so a future call can retry — most failures here are transient.
            _shareIconFilePromise = null;
            return null;
        }
    })();

    return _shareIconFilePromise;
}

function copyShareUrlFallback( url, onDone )
{
    const finalize = () =>
    {
        try { window.prompt( 'Copy this link to share Puzzlemines:', url ); } catch ( _ ) {}
        if ( typeof onDone === 'function' ) onDone();
    };

    if ( navigator.clipboard && navigator.clipboard.writeText )
    {
        navigator.clipboard.writeText( url ).then(
            () => { if ( typeof onDone === 'function' ) onDone(); },
            () => finalize()
        );
        return;
    }

    finalize();
}

function performContinueRevive()
{
    if ( !game || !mineHitCell || !preMineHitCellState )
    {
        removeContinueOverlay();
        return;
    }

    // Roll back the auto reveal-all that fired when the mine was hit.
    for ( let x = 0; x < game.width; x++ )
    {
        for ( let y = 0; y < game.height; y++ )
        {
            game.cellState[ x ][ y ] = preMineHitCellState[ x ][ y ];
        }
    }

    // Lock the hit mine tile out of play but keep it visible.
    game.frozen[ mineHitCell.x ][ mineHitCell.y ] = true;

    // Resume play, rewind the timer so the dialog isn't a time penalty.
    game.gameOver = false;
    game.won = false;
    gameOverShown = false;
    loseAnimPending = false;
    continueUsedForCurrentLevel = true;
    markCurrentLevelNotFirstTry();
    gameStartTime = time - mineHitElapsed;
    mineHitCell = null;
    preMineHitCellState = null;

    // Zoom back out to the full board so the player can see the new state.
    if ( zoomLevel > 1.01 )
    {
        zoomOutActive = true;
        zoomOutStartTime = time;
        zoomOutStartZoom = zoomLevel;
        zoomOutStartOffX = camOffsetX;
        zoomOutStartOffY = camOffsetY;
    }

    removeContinueOverlay();
}

function triggerMineHitFinale()
{
    // Original "you hit a mine" canvas effects, then fall through to
    // the normal Game Over overlay.
    shakeActive = true;
    shakeStartTime = time;
    showMineHitFlash();
    if ( navigator.vibrate ) navigator.vibrate( 200 );
    loseAnimPending = false;
}

// ============================================================
// Level intro overlay
// ============================================================

function showLevelIntroOverlay()
{
    const lvl = currentLevel;
    const num = lvl ? String( lvl.levelNumber ).padStart( 3, '0' ) : '001';
    const name = lvl ? lvl.name : 'Level';
    const difficulty = lvl ? lvl.difficulty : 'Beginner';
    const type = gameConfig ? gameConfig.type : GAME_CLEAR_ALL;
    const typeLabel = GAME_TYPE_LABELS[ type ] || 'CLEAR ALL';
    const typeGlyph = MODE_TYPE_GLYPH_LG[ type ] || MODE_TYPE_GLYPH_LG[ GAME_CLEAR_ALL ];
    const hintText = getHintForLevel( lvl );
    const diffColor = DIFFICULTY_COLOR[ difficulty ] || DIFFICULTY_COLOR.Beginner;

    const overlay = document.createElement( 'div' );
    overlay.className = 'pm-overlay pm-lvl';
    overlay.id = 'level-intro-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-lvl__card">
            <button class="pm-lvl__close" id="btn-level-back-close" aria-label="Back">${ SVG_CLOSE }</button>
            <div class="pm-lvl__eyebrow">#${ num }</div>
            <h2 class="pm-lvl__name">${ name }</h2>
            <div class="pm-lvl__chipRow">
                <span class="pm-lvl__chip" style="--chip:${ diffColor }">
                    <span class="pm-lvl__chipDot"></span>
                    ${ difficulty }
                </span>
            </div>
            <dl class="pm-lvl__details">
                <div class="pm-lvl__row">
                    <dt>Type</dt>
                    <dd class="pm-lvl__type">
                        <span class="pm-lvl__typeIc">${ typeGlyph }</span>
                        <span class="pm-lvl__typeLabel">${ typeLabel }</span>
                    </dd>
                </div>
                <div class="pm-lvl__row">
                    <dt>Grid</dt>
                    <dd class="pm-lvl__mono">${ gameConfig.w } × ${ gameConfig.h }</dd>
                </div>
                <div class="pm-lvl__row">
                    <dt>Mines</dt>
                    <dd class="pm-lvl__mono pm-lvl__minesVal">${ SVG_BOMB_MINI } ${ gameConfig.mines }</dd>
                </div>
            </dl>
            <p class="pm-lvl__hint">${ SVG_BULB }${ hintText }</p>
            <div class="pm-lvl__actions">
                <button class="pm-lvl__start" id="btn-level-start">
                    <span>Start</span>
                    ${ SVG_ARROW_RIGHT }
                </button>
                <button class="pm-lvl__back" id="btn-level-back">Back to Menu</button>
            </div>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    document.getElementById( 'btn-level-start' ).addEventListener( 'click', () =>
    {
        removeLevelIntroOverlay();
        resetGame();
    });

    const onBack = () =>
    {
        removeLevelIntroOverlay();
        if ( currentState ) currentState.app.router.redirect( '/' );
    };
    document.getElementById( 'btn-level-back' ).addEventListener( 'click', onBack );
    document.getElementById( 'btn-level-back-close' ).addEventListener( 'click', onBack );
}

function removeLevelIntroOverlay()
{
    const el = document.getElementById( 'level-intro-overlay' );
    if ( el ) el.remove();
}

// ============================================================
// Mode tutorial overlay
// ============================================================

function showModeTutorialOverlay( levelNumber, onContinue )
{
    const tutorial = MODE_TUTORIALS[ levelNumber ];
    if ( !tutorial ) { onContinue(); return; }

    const bullets = tutorial.lines.map( ( line, i ) => `
        <li class="pm-tut__item" style="animation-delay:${ 120 + i * 80 }ms">
            <span class="pm-tut__bullet" aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 1 L8 5 L2 9 Z" fill="currentColor"/></svg>
            </span>
            <span>${ line }</span>
        </li>
    `).join( '' );

    const overlay = document.createElement( 'div' );
    overlay.className = 'pm-overlay pm-tut';
    overlay.id = 'mode-tutorial-overlay';
    overlay.innerHTML = `
        <div class="pm-overlay__scrim"></div>
        <div class="pm-tut__card" style="--accent:${ tutorial.accent }">
            <div class="pm-tut__art">
                ${ tutorial.art }
                <div class="pm-tut__artRing"></div>
            </div>
            <div class="pm-tut__subtitle">${ tutorial.subtitle || 'New game mode unlocked!' }</div>
            <h2 class="pm-tut__title">${ tutorial.title }</h2>
            <ul class="pm-tut__list">
                ${ bullets }
            </ul>
            <button class="pm-lvl__start" id="btn-tutorial-continue">
                <span>Continue</span>
                ${ SVG_ARROW_RIGHT }
            </button>
        </div>
    `;
    overlay.addEventListener( 'touchstart', e => e.stopPropagation() );
    overlay.addEventListener( 'touchend',   e => e.stopPropagation() );

    document.body.appendChild( overlay );
    requestAnimationFrame( () => overlay.classList.add( 'visible' ) );

    document.getElementById( 'btn-tutorial-continue' ).addEventListener( 'click', () =>
    {
        overlay.remove();
        onContinue();
    });
}

// ============================================================
// In-game HUD
// ============================================================

const OBJECTIVE_COPY = {};
OBJECTIVE_COPY[ GAME_CLEAR_ALL ] = 'REVEAL EVERY SAFE CELL';
OBJECTIVE_COPY[ GAME_FIND ]      = 'FOLLOW THE ARROWS · FIND THE DIAMOND';
OBJECTIVE_COPY[ GAME_PATH ]      = 'CARVE A PATH FROM GREEN TO GOLD';

function showObjectivePill()
{
    removeObjectivePill();
    const mode = gameConfig ? gameConfig.type : GAME_CLEAR_ALL;
    const copy = OBJECTIVE_COPY[ mode ] || '';
    if ( !copy ) return;
    const el = document.createElement( 'div' );
    el.className = 'pm-objective';
    el.id = 'game-objective';
    el.innerHTML = `<span class="pm-objective__pill">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="6"/></svg>
        ${ copy }
    </span>`;
    document.body.appendChild( el );
}

function removeObjectivePill()
{
    const el = document.getElementById( 'game-objective' );
    if ( el ) el.remove();
}

function showMineHitFlash()
{
    const el = document.createElement( 'div' );
    el.className = 'pm-flash';
    el.id = 'game-flash';
    document.body.appendChild( el );
    setTimeout( () => el.remove(), 900 );
}

function showGameHud()
{
    removeGameHud();
    const num = currentLevel ? String( currentLevel.levelNumber ).padStart( 3, '0' ) : '001';
    const mode = gameConfig ? gameConfig.type : GAME_CLEAR_ALL;
    const modeGlyph = MODE_GLYPH_SVG[ mode ] || MODE_GLYPH_SVG[ GAME_CLEAR_ALL ];
    const maxLives = getMaxHearts();
    const lives = getLivesState().hearts;

    const heartSlots = [];
    for ( let i = 0; i < maxLives; i++ )
    {
        heartSlots.push(
            `<span class="pm-hud__heartSlot" style="--i:${ i }">${ i < lives ? SVG_HEART_HUD_FULL : SVG_HEART_HUD_EMPTY }</span>`
        );
    }

    const el = document.createElement( 'header' );
    el.className = 'pm-hud';
    el.id = 'game-hud';
    el.innerHTML = `
        <div class="pm-hud__left">
            <div class="pm-hud__level">
                <span class="pm-hud__levelEyebrow">LEVEL</span>
                <span class="pm-hud__levelNum">#${ num }</span>
                <span class="pm-hud__levelMode">${ modeGlyph }</span>
            </div>
        </div>
        <div class="pm-hud__center">
            <div class="pm-hud__chip pm-hud__chip--flags" id="hud-chip-flags">
                ${ SVG_FLAG }
                <span class="pm-hud__chipVal">
                    <span class="pm-hud__flagsUsed" id="hud-flags-used">0</span><span class="pm-hud__flagsSep">/</span><span class="pm-hud__flagsTotal">${ gameConfig.mines }</span>
                </span>
            </div>
            <div class="pm-hud__chip pm-hud__chip--timer" id="hud-chip-timer">
                ${ SVG_CLOCK }
                <span class="pm-hud__chipVal pm-hud__time" id="hud-timer">0:00</span>
            </div>
        </div>
        <div class="pm-hud__right">
            <div class="pm-hud__hearts" id="hud-hearts">
                ${ heartSlots.join( '' ) }
            </div>
        </div>
    `;
    document.body.appendChild( el );
    hudElement = el;

    showObjectivePill();
}

function updateGameHud()
{
    if ( !game || !hudElement ) return;

    // Update flagged mine count
    const flagsUsedEl = document.getElementById( 'hud-flags-used' );
    const flagsChip   = document.getElementById( 'hud-chip-flags' );
    if ( flagsUsedEl )
    {
        let flagged = 0;
        for ( let x = 0; x < game.width; x++ )
            for ( let y = 0; y < game.height; y++ )
                if ( game.cellState[ x ][ y ] === CELL_FLAGGED ) flagged++;
        flagsUsedEl.textContent = flagged;
        if ( flagsChip )
        {
            flagsChip.classList.toggle( 'is-danger', flagged > gameConfig.mines );
        }
    }

    if ( game.gameOver ) return;

    const timerEl   = document.getElementById( 'hud-timer' );
    const timerChip = document.getElementById( 'hud-chip-timer' );
    if ( !timerEl ) return;
    const elapsed = Math.min( 999, Math.max( 0, Math.floor( time - gameStartTime ) ) );
    const m = Math.floor( elapsed / 60 );
    const s = elapsed % 60;
    timerEl.textContent = m + ':' + String( s ).padStart( 2, '0' );

    if ( timerChip )
    {
        timerChip.classList.toggle( 'is-danger', elapsed >= TIMER_DANGER_THRESHOLD );
    }
}

function removeGameHud()
{
    const el = document.getElementById( 'game-hud' );
    if ( el ) el.remove();
    hudElement = null;
    removeObjectivePill();
}

// ============================================================
// Pinch-zoom + touch handlers
// ============================================================

function getTouchDist( e )
{
    const a = e.touches[ 0 ];
    const b = e.touches[ 1 ];
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.sqrt( dx * dx + dy * dy );
}

function onTouchStart( e )
{
    if ( e.touches.length >= 2 )
    {
        pinchActive = true;
        pinchStartDist = getTouchDist( e );
        pinchStartZoom = zoomLevel;
        pressStartTime = null;
        longPressTriggered = false;
        isDragging = false;
    }
}

function onTouchMove( e )
{
    if ( pinchActive && e.touches.length >= 2 )
    {
        const dist = getTouchDist( e );
        zoomLevel = clamp( pinchStartZoom * ( dist / pinchStartDist ), ZOOM_MIN, ZOOM_MAX );
    }
}

function onTouchEnd( e )
{
    if ( pinchActive && e.touches.length < 2 )
    {
        pinchActive = false;
        pinchCooldown = 10;
    }
}

// ============================================================
// Camera
// ============================================================

function updateCamera()
{
    if ( !mainCanvas || mainCanvas.width <= 0 ) return;

    const gw = gameConfig.w;
    const gh = gameConfig.h;

    baseScale = Math.min(
        mainCanvas.width  / ( gw + 2 ),
        mainCanvas.height / ( gh + 1 )
    );
    const scale = baseScale * zoomLevel;
    setCameraScale( scale );

    if ( zoomLevel > 1.01 )
    {
        const visW = mainCanvas.width  / scale;
        const visH = mainCanvas.height / scale;
        const maxOffX = Math.max( 0, ( gw - visW ) / 2 );
        const maxOffYDown = Math.max( 0, ( gh - visH ) / 2 );

        // Allow extra upward pan equal to the HUD bar height, so the top row
        // can be scrolled below the fixed HUD and stay fully visible.
        const hudEl = document.getElementById( 'game-hud' );
        const hudPx = hudEl ? hudEl.getBoundingClientRect().height : 80;
        const hudWorld = hudPx / scale;
        const maxOffYUp = maxOffYDown + hudWorld;

        camOffsetX = clamp( camOffsetX, -maxOffX, maxOffX );
        camOffsetY = clamp( camOffsetY, -maxOffYDown, maxOffYUp );
    }
    else
    {
        camOffsetX = 0;
        camOffsetY = 0;
    }

    let shakeX = 0, shakeY = 0;
    if ( shakeActive )
    {
        const elapsed = time - shakeStartTime;
        if ( elapsed >= SHAKE_DURATION )
        {
            shakeActive = false;
        }
        else
        {
            const decay = 1 - elapsed / SHAKE_DURATION;
            shakeX = ( Math.random() * 2 - 1 ) * SHAKE_INTENSITY * decay;
            shakeY = ( Math.random() * 2 - 1 ) * SHAKE_INTENSITY * decay;
        }
    }

    setCameraPos( vec2( gw / 2 + camOffsetX + shakeX, gh / 2 + camOffsetY + shakeY ) );
}

// ============================================================
// LittleJS callbacks
// ============================================================

function onGameInit()
{
    setCameraPos( vec2( gameConfig.w / 2, gameConfig.h / 2 ) );
}

function onGameUpdate()
{
    if ( !game ) return;

    if ( zoomOutActive )
    {
        const elapsed = time - zoomOutStartTime;
        const t = clamp( elapsed / ZOOM_OUT_DURATION, 0, 1 );
        const ease = 1 - ( 1 - t ) * ( 1 - t );
        zoomLevel = zoomOutStartZoom + ( 1 - zoomOutStartZoom ) * ease;
        camOffsetX = zoomOutStartOffX * ( 1 - ease );
        camOffsetY = zoomOutStartOffY * ( 1 - ease );
        if ( t >= 1 ) zoomOutActive = false;
    }

    if ( zoomToCellActive )
    {
        const elapsed = time - zoomToCellStartTime;
        const t = clamp( elapsed / ZOOM_TO_CELL_DURATION, 0, 1 );
        const ease = 1 - ( 1 - t ) * ( 1 - t );
        zoomLevel = zoomToCellStartZoom + ( zoomToCellTargetZoom - zoomToCellStartZoom ) * ease;
        camOffsetX = zoomToCellStartOffX + ( zoomToCellTargetOffX - zoomToCellStartOffX ) * ease;
        camOffsetY = zoomToCellStartOffY + ( zoomToCellTargetOffY - zoomToCellStartOffY ) * ease;
        if ( t >= 1 )
        {
            zoomToCellActive = false;
            const cb = zoomToCellCallback;
            zoomToCellCallback = null;
            if ( cb ) cb();
        }
    }

    updateCamera();

    if ( pinchCooldown > 0 )
    {
        pinchCooldown--;
        pressStartTime = null;
        return;
    }

    if ( pinchActive ) return;

    if ( mouseWheel )
    {
        zoomLevel = clamp( zoomLevel * ( 1 - mouseWheel * 0.15 ), ZOOM_MIN, ZOOM_MAX );
    }

    if ( game.isAnimating() )
    {
        game.processPendingReveals( 5, time );
        return;
    }

    if ( game.gameOver ) return;

    if ( mouseWasPressed( 2 ) )
    {
        const cell = gridFromPos( mousePos );
        if ( cell ) game.toggleFlag( cell.x, cell.y );
    }

    if ( mouseWasPressed( 0 ) )
    {
        pressStartTime = Date.now();
        pressStartPos = mousePos.copy();
        pressStartScreen = mousePosScreen.copy();
        longPressTriggered = false;
        isDragging = false;
    }

    if ( mouseIsDown( 0 ) && pressStartTime !== null )
    {
        if ( !isDragging && !longPressTriggered && zoomLevel > 1.01 )
        {
            const dx = mousePosScreen.x - pressStartScreen.x;
            const dy = mousePosScreen.y - pressStartScreen.y;
            if ( dx * dx + dy * dy > DRAG_THRESHOLD * DRAG_THRESHOLD )
            {
                isDragging = true;
                lastDragScreen = mousePosScreen.copy();
            }
        }

        if ( isDragging )
        {
            const scale = baseScale * zoomLevel;
            const dx = mousePosScreen.x - lastDragScreen.x;
            const dy = mousePosScreen.y - lastDragScreen.y;
            camOffsetX -= dx / scale;
            camOffsetY += dy / scale;
            lastDragScreen = mousePosScreen.copy();
        }

        if ( !longPressTriggered && !isDragging )
        {
            if ( Date.now() - pressStartTime >= LONG_PRESS_MS )
            {
                const cell = gridFromPos( pressStartPos );
                if ( cell ) game.toggleFlag( cell.x, cell.y );
                longPressTriggered = true;
            }
        }
    }

    if ( mouseWasReleased( 0 ) && pressStartTime !== null )
    {
        if ( !longPressTriggered && !isDragging )
        {
            const cell = gridFromPos( pressStartPos );
            if ( cell )
            {
                // Snapshot cellState in case this is a mine hit and the player
                // chooses to Continue — we need to roll back the auto reveal-all.
                const willHitMine =
                    !game.gameOver
                    && game.cellState[ cell.x ][ cell.y ] === CELL_HIDDEN
                    && game.mines[ cell.x ][ cell.y ]
                    && game.canReveal( cell.x, cell.y );
                let snapshot = null;
                if ( willHitMine )
                {
                    snapshot = game.cellState.map( col => col.slice() );
                }

                game.reveal( cell.x, cell.y );
                if ( game.gameOver && !game.won )
                {
                    mineHitCell = { x: cell.x, y: cell.y };
                    preMineHitCellState = snapshot;
                    mineHitElapsed = time - gameStartTime;
                    loseAnimPending = true;
                    zoomOutActive = false;
                    zoomToCellActive = true;
                    zoomToCellStartTime = time;
                    zoomToCellStartZoom = zoomLevel;
                    zoomToCellStartOffX = camOffsetX;
                    zoomToCellStartOffY = camOffsetY;
                    zoomToCellTargetZoom = Math.min( ZOOM_MAX, ZOOM_TO_CELL_TARGET );
                    zoomToCellTargetOffX = ( cell.x + 0.5 ) - gameConfig.w / 2;
                    zoomToCellTargetOffY = ( cell.y + 0.5 ) - gameConfig.h / 2;
                    zoomToCellCallback = () =>
                    {
                        if ( !continueUsedForCurrentLevel && preMineHitCellState )
                        {
                            // Show the Continue dialog instead of running the
                            // shake / flash / game-over flow. The dialog's
                            // handlers decide what happens next.
                            showContinueOverlay();
                        }
                        else
                        {
                            triggerMineHitFinale();
                        }
                    };
                }
            }
        }
        pressStartTime = null;
        longPressTriggered = false;
        isDragging = false;
    }
}

function onGameUpdatePost()
{
    updateGameHud();
}

// ============================================================
// Sprite atlas — pre-renders every cell variant once, then the
// per-frame render loop swaps in batched drawTile() calls.
//
// Atlas layout (8 cols × 5 rows, 64px/tile → 512×320):
//   row 0: HIDDEN, FLAGGED, REVEALED_A, REVEALED_B, MINE, DIAMOND_A, DIAMOND_B, —
//   row 1: NUM_1_A … NUM_8_A
//   row 2: NUM_1_B … NUM_8_B
//   row 3: ARROW_0_A … ARROW_7_A   (→ ↗ ↑ ↖ ← ↙ ↓ ↘)
//   row 4: ARROW_0_B … ARROW_7_B
// ============================================================

const SPRITE_SIZE = 64;          // px per tile in the atlas
const ATLAS_COLS  = 8;
const ATLAS_ROWS  = 5;
const ARROW_CHARS = [ '→', '↗', '↑', '↖', '←', '↙', '↓', '↘' ];

let atlasReady   = false;
let spriteTex    = null;
const spriteTiles = {
    HIDDEN:  null,
    FLAGGED: null,
    REVEALED_A: null, REVEALED_B: null,
    MINE:    null,
    DIAMOND_A: null, DIAMOND_B: null,
    FROZEN:  null,
    NUM:   [ null, null, null, null, null, null, null, null, null ],  // index by count, 0 unused
    ARROW: { A: [], B: [] },                                           // index by arrow index 0..7
};

function spriteOrigin( idx )
{
    const col = idx % ATLAS_COLS;
    const row = Math.floor( idx / ATLAS_COLS );
    return { x: col * SPRITE_SIZE, y: row * SPRITE_SIZE };
}

function fillTopStrip( ctx, x, y, color )
{
    ctx.fillStyle = color.toString();
    ctx.fillRect( x + 4, y + 1, SPRITE_SIZE - 8, 3 );
}
function fillBotStrip( ctx, x, y, color )
{
    ctx.fillStyle = color.toString();
    ctx.fillRect( x + 4, y + SPRITE_SIZE - 4, SPRITE_SIZE - 8, 3 );
}

function drawBeveledTileSprite( ctx, x, y, topColor, botColor )
{
    const grad = ctx.createLinearGradient( 0, y, 0, y + SPRITE_SIZE );
    grad.addColorStop( 0, topColor.toString() );
    grad.addColorStop( 1, botColor.toString() );
    ctx.fillStyle = grad;
    ctx.fillRect( x, y, SPRITE_SIZE, SPRITE_SIZE );
    fillTopStrip( ctx, x, y, COLOR_HIDDEN_RIM_HI );
    fillBotStrip( ctx, x, y, COLOR_HIDDEN_RIM_LO );
}

function drawRevealedBaseSprite( ctx, x, y, parity )
{
    const bg = parity ? COLOR_REVEALED_B : COLOR_REVEALED_A;
    ctx.fillStyle = bg.toString();
    ctx.fillRect( x, y, SPRITE_SIZE, SPRITE_SIZE );
    // Top inset shadow — sunken feel
    ctx.fillStyle = COLOR_REVEALED_INSET.toString();
    ctx.fillRect( x + 4, y + 1, SPRITE_SIZE - 8, 3 );
}

function drawFrozenSprite( ctx, x, y )
{
    // Muted putty plate — recessed feel, "out of play"
    const grad = ctx.createLinearGradient( 0, y, 0, y + SPRITE_SIZE );
    grad.addColorStop( 0, '#B8AFA4' );
    grad.addColorStop( 1, '#A89F94' );
    ctx.fillStyle = grad;
    ctx.fillRect( x, y, SPRITE_SIZE, SPRITE_SIZE );
    // Top highlight
    ctx.fillStyle = 'rgba(255,255,255,0.30)';
    ctx.fillRect( x + 4, y + 1, SPRITE_SIZE - 8, 3 );
    // Bottom shadow
    ctx.fillStyle = 'rgba(45,37,32,0.32)';
    ctx.fillRect( x + 4, y + SPRITE_SIZE - 4, SPRITE_SIZE - 8, 3 );
    // Inner shadow band — sunken plate
    ctx.fillStyle = 'rgba(45,37,32,0.20)';
    ctx.fillRect( x + 4, y + 4, SPRITE_SIZE - 8, 5 );

    const cx = x + SPRITE_SIZE / 2;
    const cy = y + SPRITE_SIZE / 2;

    // Faint bomb silhouette behind the lock
    ctx.fillStyle = 'rgba(45,37,32,0.30)';
    ctx.beginPath();
    ctx.arc( cx, cy + 2, SPRITE_SIZE * 0.40, 0, Math.PI * 2 );
    ctx.fill();
    // Spikes around the rim, also faint
    ctx.strokeStyle = 'rgba(45,37,32,0.22)';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    const r0 = SPRITE_SIZE * 0.46;
    const r1 = SPRITE_SIZE * 0.36;
    const spikes = [
        [ 0, -1 ], [ 0, 1 ], [ -1, 0 ], [ 1, 0 ],
        [ -0.7071, -0.7071 ], [ 0.7071, 0.7071 ],
        [ -0.7071, 0.7071 ], [ 0.7071, -0.7071 ],
    ];
    for ( const [ ux, uy ] of spikes )
    {
        ctx.beginPath();
        ctx.moveTo( cx + ux * r0, cy + uy * r0 );
        ctx.lineTo( cx + ux * r1, cy + uy * r1 );
        ctx.stroke();
    }

    // Padlock — solid, high-contrast (the hero of the cell)
    const ink = '#2D2520';
    const inkDeep = '#14100C';

    // Shackle (open arch above the body)
    ctx.strokeStyle = ink;
    ctx.lineWidth = SPRITE_SIZE * 0.10;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc( cx, cy - SPRITE_SIZE * 0.06, SPRITE_SIZE * 0.17, Math.PI, 0 );
    ctx.stroke();

    // Body
    const bodyW = SPRITE_SIZE * 0.50;
    const bodyH = SPRITE_SIZE * 0.36;
    const bodyX = cx - bodyW / 2;
    const bodyY = cy - SPRITE_SIZE * 0.04;
    ctx.fillStyle = ink;
    ctx.strokeStyle = inkDeep;
    ctx.lineWidth = 1;
    const br = 5;
    ctx.beginPath();
    ctx.moveTo( bodyX + br, bodyY );
    ctx.lineTo( bodyX + bodyW - br, bodyY );
    ctx.quadraticCurveTo( bodyX + bodyW, bodyY, bodyX + bodyW, bodyY + br );
    ctx.lineTo( bodyX + bodyW, bodyY + bodyH - br );
    ctx.quadraticCurveTo( bodyX + bodyW, bodyY + bodyH, bodyX + bodyW - br, bodyY + bodyH );
    ctx.lineTo( bodyX + br, bodyY + bodyH );
    ctx.quadraticCurveTo( bodyX, bodyY + bodyH, bodyX, bodyY + bodyH - br );
    ctx.lineTo( bodyX, bodyY + br );
    ctx.quadraticCurveTo( bodyX, bodyY, bodyX + br, bodyY );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Keyhole
    const khR = SPRITE_SIZE * 0.06;
    const khCY = bodyY + bodyH * 0.34;
    ctx.fillStyle = '#FAF4E6';
    ctx.beginPath();
    ctx.arc( cx, khCY, khR, 0, Math.PI * 2 );
    ctx.fill();
    ctx.fillRect( cx - khR * 0.55, khCY, khR * 1.1, bodyH * 0.40 );
}

function drawEmbossedChar( ctx, x, y, ch, fillColor, fontPx )
{
    const cx = x + SPRITE_SIZE / 2;
    const cy = y + SPRITE_SIZE / 2 + 3;   // nudge down to visually center in font em-box
    ctx.font = `${ CANVAS_FONT_STYLE } ${ fontPx }px ${ CANVAS_FONT }`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // white "below" shadow — mimics CSS `text-shadow: 0 1px 0 rgba(255,255,255,0.35)`
    ctx.fillStyle = COLOR_EMBOSS_HIGHLIGHT.toString();
    ctx.fillText( ch, cx, cy + 2 );
    // main fill
    ctx.fillStyle = fillColor.toString();
    ctx.fillText( ch, cx, cy );
}

function drawPlainChar( ctx, x, y, ch, fillColor, fontPx, strokeColor, strokeWidth )
{
    const cx = x + SPRITE_SIZE / 2;
    const cy = y + SPRITE_SIZE / 2 + 3;
    ctx.font = `${ CANVAS_FONT_STYLE } ${ fontPx }px ${ CANVAS_FONT }`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if ( strokeColor && strokeWidth )
    {
        ctx.strokeStyle = strokeColor.toString();
        ctx.lineWidth = strokeWidth;
        ctx.lineJoin = 'round';
        ctx.strokeText( ch, cx, cy );
    }
    ctx.fillStyle = fillColor.toString();
    ctx.fillText( ch, cx, cy );
}

function mkTile( idx )
{
    const o = spriteOrigin( idx );
    return new TileInfo( vec2( o.x, o.y ), vec2( SPRITE_SIZE ), spriteTex );
}

async function buildSpriteAtlas()
{
    // Make sure Nunito is loaded before rasterising
    if ( document.fonts && document.fonts.load )
    {
        try {
            await document.fonts.load( `${ CANVAS_FONT_STYLE } 40px ${ CANVAS_FONT }` );
            await document.fonts.load( `${ CANVAS_FONT_STYLE } 48px ${ CANVAS_FONT }` );
            if ( document.fonts.ready ) await document.fonts.ready;
        } catch (e) { /* continue with fallback font */ }
    }

    const canvas = document.createElement( 'canvas' );
    canvas.width  = ATLAS_COLS * SPRITE_SIZE;
    canvas.height = ATLAS_ROWS * SPRITE_SIZE;
    const ctx = canvas.getContext( '2d' );
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // ---- row 0 utility sprites ----
    let o;
    o = spriteOrigin( 0 ); drawBeveledTileSprite( ctx, o.x, o.y, COLOR_HIDDEN_TOP, COLOR_HIDDEN_BOT );
    o = spriteOrigin( 1 );
    drawBeveledTileSprite( ctx, o.x, o.y, COLOR_FLAG_TOP, COLOR_FLAG_BOT );
    drawPlainChar( ctx, o.x, o.y, '⚑', COLOR_FLAG_TEXT, 40 );
    o = spriteOrigin( 2 ); drawRevealedBaseSprite( ctx, o.x, o.y, false );
    o = spriteOrigin( 3 ); drawRevealedBaseSprite( ctx, o.x, o.y, true );
    o = spriteOrigin( 4 );
    ctx.fillStyle = COLOR_MINE_BG.toString();
    ctx.fillRect( o.x, o.y, SPRITE_SIZE, SPRITE_SIZE );
    drawPlainChar( ctx, o.x, o.y, '✹', COLOR_MINE_TEXT, 38 );
    o = spriteOrigin( 5 );
    drawRevealedBaseSprite( ctx, o.x, o.y, false );
    drawPlainChar( ctx, o.x, o.y, '◆', COLOR_DIAMOND, 46, COLOR_FRAME_OUTER, 2 );
    o = spriteOrigin( 6 );
    drawRevealedBaseSprite( ctx, o.x, o.y, true );
    drawPlainChar( ctx, o.x, o.y, '◆', COLOR_DIAMOND, 46, COLOR_FRAME_OUTER, 2 );
    o = spriteOrigin( 7 ); drawFrozenSprite( ctx, o.x, o.y );

    // ---- row 1/2: numbers 1..8 × parity A/B ----
    for ( let n = 1; n <= 8; n++ )
    {
        const idxA = ATLAS_COLS + ( n - 1 );
        const idxB = ATLAS_COLS * 2 + ( n - 1 );
        const oA = spriteOrigin( idxA );
        const oB = spriteOrigin( idxB );
        drawRevealedBaseSprite( ctx, oA.x, oA.y, false );
        drawEmbossedChar( ctx, oA.x, oA.y, String( n ), NUMBER_COLORS[ n ], 40 );
        drawRevealedBaseSprite( ctx, oB.x, oB.y, true );
        drawEmbossedChar( ctx, oB.x, oB.y, String( n ), NUMBER_COLORS[ n ], 40 );
    }

    // ---- row 3/4: arrows × parity A/B ----
    for ( let d = 0; d < 8; d++ )
    {
        const idxA = ATLAS_COLS * 3 + d;
        const idxB = ATLAS_COLS * 4 + d;
        const oA = spriteOrigin( idxA );
        const oB = spriteOrigin( idxB );
        drawRevealedBaseSprite( ctx, oA.x, oA.y, false );
        drawPlainChar( ctx, oA.x, oA.y, ARROW_CHARS[ d ], COLOR_ARROW, 40 );
        drawRevealedBaseSprite( ctx, oB.x, oB.y, true );
        drawPlainChar( ctx, oB.x, oB.y, ARROW_CHARS[ d ], COLOR_ARROW, 40 );
    }

    spriteTex = new TextureInfo( canvas );

    spriteTiles.HIDDEN     = mkTile( 0 );
    spriteTiles.FLAGGED    = mkTile( 1 );
    spriteTiles.REVEALED_A = mkTile( 2 );
    spriteTiles.REVEALED_B = mkTile( 3 );
    spriteTiles.MINE       = mkTile( 4 );
    spriteTiles.DIAMOND_A  = mkTile( 5 );
    spriteTiles.DIAMOND_B  = mkTile( 6 );
    spriteTiles.FROZEN     = mkTile( 7 );
    for ( let n = 1; n <= 8; n++ )
    {
        spriteTiles.NUM[ n ] = { A: mkTile( ATLAS_COLS + ( n - 1 ) ), B: mkTile( ATLAS_COLS * 2 + ( n - 1 ) ) };
    }
    for ( let d = 0; d < 8; d++ )
    {
        spriteTiles.ARROW.A[ d ] = mkTile( ATLAS_COLS * 3 + d );
        spriteTiles.ARROW.B[ d ] = mkTile( ATLAS_COLS * 4 + d );
    }

    atlasReady = true;
}

// ============================================================
// Rendering helpers
// ============================================================

/** Draw a rounded rectangle by stacking two crossed rects + 4 corner circles. */
function drawRoundedRect( pos, size, radius, color )
{
    const r = Math.min( radius, size.x / 2, size.y / 2 );
    drawRect( pos, vec2( size.x, size.y - 2 * r ), color );
    drawRect( pos, vec2( size.x - 2 * r, size.y ), color );
    const ix = size.x / 2 - r;
    const iy = size.y / 2 - r;
    drawCircle( vec2( pos.x - ix, pos.y - iy ), r * 2, color );
    drawCircle( vec2( pos.x + ix, pos.y - iy ), r * 2, color );
    drawCircle( vec2( pos.x - ix, pos.y + iy ), r * 2, color );
    drawCircle( vec2( pos.x + ix, pos.y + iy ), r * 2, color );
}

// WHITE color used for drawTile color modulation
const COLOR_WHITE = new Color( 1, 1, 1 );

function onGameRender()
{
    if ( !game ) return;

    const gw = gameConfig.w;
    const gh = gameConfig.h;

    // ---- Playfield sage backdrop fills the whole camera view ----
    const scale = baseScale * zoomLevel || 32;
    const camCx = gw / 2 + camOffsetX;
    const camCy = gh / 2 + camOffsetY;
    const visW  = ( mainCanvas ? mainCanvas.width  / scale : gw + 4 );
    const visH  = ( mainCanvas ? mainCanvas.height / scale : gh + 4 );
    drawRect( vec2( camCx, camCy ), vec2( visW + 2, visH + 2 ), COLOR_PLAYFIELD );

    // ---- Rounded dark board frame (outer ink + warmer inner recess) ----
    const boardCenter = vec2( gw / 2, gh / 2 );
    const outerSize = vec2( gw + FRAME_PAD, gh + FRAME_PAD );
    const innerSize = vec2( gw + INNER_PAD, gh + INNER_PAD );
    drawRoundedRect( boardCenter, outerSize, FRAME_RAD, COLOR_FRAME_OUTER );
    drawRoundedRect( boardCenter, innerSize, FRAME_RAD * 0.8, COLOR_FRAME_INNER );

    // ---- PATH zones (under cells) ----
    if ( gameConfig.type === GAME_PATH )
    {
        drawRect( vec2( gw / 2, 0.5 ), vec2( gw, 1 ), COLOR_START_ZONE );
        drawRect( vec2( gw / 2, gh - 0.5 ), vec2( gw, 1 ), COLOR_FINISH_ZONE );
    }

    // Atlas not ready yet — skip cells for this frame (backdrop + frame still render).
    if ( !atlasReady ) return;

    const cellWorld = vec2( CELL_SIZE, CELL_SIZE );

    // ---- Cells (all via drawTile for batched WebGL rendering) ----
    for ( let x = 0; x < gw; x++ )
    {
        for ( let y = 0; y < gh; y++ )
        {
            const cell = game.getCell( x, y );
            if ( cell.disabled ) continue;

            const pos = vec2( x + 0.5, y + 0.5 );
            const parity = ( ( x + y ) % 2 !== 0 );   // false=A, true=B
            let tile;

            if ( cell.frozen )
            {
                tile = spriteTiles.FROZEN;
            }
            else if ( cell.state === CELL_HIDDEN )
            {
                tile = spriteTiles.HIDDEN;
            }
            else if ( cell.state === CELL_FLAGGED )
            {
                tile = spriteTiles.FLAGGED;
            }
            else if ( cell.hasMine )
            {
                tile = spriteTiles.MINE;
            }
            else
            {
                const isDiamond = gameConfig.type === GAME_FIND && x === game.diamondX && y === game.diamondY;
                if ( isDiamond )
                {
                    tile = parity ? spriteTiles.DIAMOND_B : spriteTiles.DIAMOND_A;
                }
                else if ( gameConfig.type === GAME_FIND && cell.adjacentCount === 0 && game.diamondX >= 0 )
                {
                    const arrow = game.getDirectionToDiamond( x, y );
                    const d = ARROW_CHARS.indexOf( arrow );
                    if ( d >= 0 )
                    {
                        tile = parity ? spriteTiles.ARROW.B[ d ] : spriteTiles.ARROW.A[ d ];
                    }
                    else
                    {
                        tile = parity ? spriteTiles.REVEALED_B : spriteTiles.REVEALED_A;
                    }
                }
                else if ( cell.adjacentCount > 0 )
                {
                    tile = parity ? spriteTiles.NUM[ cell.adjacentCount ].B
                                  : spriteTiles.NUM[ cell.adjacentCount ].A;
                }
                else
                {
                    tile = parity ? spriteTiles.REVEALED_B : spriteTiles.REVEALED_A;
                }
            }

            drawTile( pos, cellWorld, tile, COLOR_WHITE );

            // Flash glow on newly-revealed cells (still procedural — tiny cost)
            if ( cell.state === CELL_REVEALED && !cell.hasMine )
            {
                const age = time - game.revealTimestamps[ x ][ y ];
                if ( age < 0.2 )
                {
                    drawRect( pos, cellWorld, new Color( 1, 1, 1, ( 1 - age / 0.2 ) * 0.45 ) );
                }
            }
        }
    }
}

function onGameRenderPost()
{
    if ( !game ) return;

    if ( game.gameOver && !gameOverShown && !loseAnimPending )
    {
        gameOverShown = true;

        let livesAfterLoss;
        if ( !game.won )
        {
            const result = loseHeart();
            livesAfterLoss = result.hearts;
            // Update HUD hearts row visually
            const heartsEl = document.getElementById( 'hud-hearts' );
            if ( heartsEl )
            {
                const maxLives = getMaxHearts();
                let html = '';
                for ( let i = 0; i < maxLives; i++ )
                {
                    html += `<span class="pm-hud__heartSlot" style="--i:${ i }">${ i < livesAfterLoss ? SVG_HEART_HUD_FULL : SVG_HEART_HUD_EMPTY }</span>`;
                }
                heartsEl.innerHTML = html;
            }
        }

        removeGameHud();
        showGameOverOverlay( game.won, livesAfterLoss );
    }
}

// ============================================================
// InfrontJS State
// ============================================================

export class GameState extends IF.State
{
    static ID = 'game';
    static ROUTE = '/game';

    async enter()
    {
        currentState = this;

        if ( getLivesState().hearts <= 0 )
        {
            this.app.router.redirect( '/' );
            return;
        }

        trackPageView( 'GameState', '/game' );

        const gameContainer = document.getElementById( 'game-container' );
        gameContainer.classList.add( 'active' );
        document.getElementById( 'app' ).classList.remove( 'active' );

        loadNextLevel();

        if ( !engineInitialized )
        {
            engineInitialized = true;

            setShowSplashScreen( false );
            setTouchGamepadEnable( false );
            setSoundEnable( false );
            setCanvasMaxSize( vec2( 600, 900 ) );
            setCanvasClearColor( COLOR_PLAYFIELD );
            setDebugWatermark( false );

            gameContainer.addEventListener( 'contextmenu', e => e.preventDefault() );

            gameContainer.addEventListener( 'touchstart', onTouchStart, { passive: true } );
            gameContainer.addEventListener( 'touchmove',  onTouchMove,  { passive: true } );
            gameContainer.addEventListener( 'touchend',   onTouchEnd,   { passive: true } );

            engineInit(
                onGameInit,
                onGameUpdate,
                onGameUpdatePost,
                onGameRender,
                onGameRenderPost,
                [],
                gameContainer
            );

            // LittleJS's engineInit sets rootElement.style.cssText to
            // `margin:0;overflow:hidden;background:#000;...` which wipes our
            // CSS class styling. Restore the sage backdrop.
            gameContainer.style.background = 'var(--bg-sage)';

            // Build the sprite atlas now that WebGL is ready.
            // Cells render blank for a frame or two until this promise resolves.
            buildSpriteAtlas().catch( err => console.error( 'sprite atlas build failed', err ) );
        }
    }

    async exit()
    {
        removeGameHud();
        removeGameOverOverlay();
        removeLevelIntroOverlay();
        removeContinueOverlay();
        const tut = document.getElementById( 'mode-tutorial-overlay' );
        if ( tut ) tut.remove();
        const mm = document.getElementById( 'mines-master-overlay' );
        if ( mm ) mm.remove();
        document.getElementById( 'game-container' ).classList.remove( 'active' );
    }
}
